import Box from "@mui/material/Box";
import { cleanProps } from "../internal/cleanProps";
import { tokens } from "../tokens";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ContentPanelProps, ContentPanelVariant } from "../types";

/**
 * H2's own white content card (README.md "Global shell", "Card": white,
 * radius 8px, `box-shadow: 0 2px 8px rgba(15,50,83,0.10)`, generous
 * padding, a vertical stack of whatever the screen needs — eyebrow,
 * heading, fields, actions) — e.g. `03-RenewalFlow.dc.html:277-278`'s
 * `card` style, `04-05-GuidedExposures.dc.html:2251-2252`'s `sec.card`,
 * `06-Declaration.dc.html:300-301`'s `card`. Every H2 flow screen wraps
 * its own page content in exactly this card; the caller (not this
 * component) decides what sits inside it versus above it — H2's own
 * convention is the back link and, on some screens, the eyebrow/heading
 * group sit above the card, never inside it (README.md "Back link —
 * above the card").
 *
 * Not generic upstream (composed from `Box`, no polymorphic root) — no
 * containment note beyond "no Material UI type crosses the boundary"
 * applies. Deliberately generic and screen-agnostic, unlike
 * `RenewalSummaryCard` (which always renders its own "Renewal summary"
 * eyebrow/business-name head) and `SectionListCard` (which owns a fixed
 * heading/row/footer structure) — this component owns no content of its
 * own at all, matching `RenewalSummaryCard.tsx`'s own token values
 * (`tokens.interactionState.elevation.card`, `radius.surface`,
 * `spacing.lg`) so every H2 card in the portal reads as one surface.
 */
const VARIANT_SX: Record<ContentPanelVariant, SxProps<Theme>> = {
  flow: { padding: { xs: "20px", sm: `${tokens.spacing.xl}px` }, gap: `${tokens.spacing.lg}px` },
  declaration: { padding: { xs: `${tokens.spacing.lg}px`, sm: `${tokens.spacing.xl}px` }, gap: `${tokens.spacing.lg}px` },
  summary: { padding: "clamp(20px, 4vw, 32px)", gap: "20px" },
};

export function ContentPanel({ children, testId, variant }: Readonly<ContentPanelProps>) {
  return (
    <Box
      {...cleanProps({ "data-testid": testId })}
      sx={{
        backgroundColor: tokens.colour.neutral.white,
        borderRadius: `${tokens.interactionState.radius.surface}px`,
        boxShadow: tokens.interactionState.elevation.card,
        padding: `${tokens.spacing.lg}px`,
        display: "flex",
        flexDirection: "column",
        gap: `${tokens.spacing.lg}px`,
        ...(variant === undefined ? {} : VARIANT_SX[variant]),
      }}
    >
      {children}
    </Box>
  );
}
